import Axios, { AxiosInstance } from "axios";
import * as JWT from "jsonwebtoken";

import type {
  AccessToken,
  CreateMagicLinkRequest,
  CreatePasscodeRequest,
  CreateUserRequest,
  CreateUserResponse,
  ILoginSuccessResponse,
  ITokenSuccessResponse,
  JsonWebTokenPayload,
  LoginRequest,
  LoginSuccessResponse,
  MagicLinkInvalidResponse,
  UseMagicLinkRequest,
  VerifyLoginPasscodeDto,
} from "@auth/types";
import { TokenRequest } from "@auth/types";

import { MagicLinkCreationResult } from "./types";

export class AuthSDK {

  private readonly axios: AxiosInstance;

  constructor(
    baseURLAuth: string,
  ) {
    this.axios = Axios.create({ baseURL: baseURLAuth });
  }

  public async loginWithEmailPassword(
    email: string,
    password: string,
  ): Promise<ILoginSuccessResponse> {

    const data: LoginRequest = { grant_type: "password", username: email, password };
    const response = await this.axios.post<ILoginSuccessResponse>(
      "login",
      data,
    );

    return response.data;
  }

  public async sendMagicLink(email: string, redirectTo?: string): Promise<MagicLinkCreationResult> {

    const data: CreateMagicLinkRequest = { email, redirectTo };

    try {
      await this.axios.put(
        "magic-link",
        data,
      );

      return MagicLinkCreationResult.SUCCESS;
    } catch (err) {
      if (Axios.isAxiosError(err)) {
        if (err.response?.status === 404) return MagicLinkCreationResult.NOT_FOUND;
        return MagicLinkCreationResult.OTHER_ERROR;
      }

      return MagicLinkCreationResult.OTHER_ERROR;
    }
  }

  public async sendPasscode(email: string, redirectTo?: string): Promise<void> {
    const data: CreatePasscodeRequest = { email, redirectTo };

    const notFound = 404;
    const conflict = 409;

    try {
      await this.axios.post("login-passcode", data);
    } catch (err) {

      if (!Axios.isAxiosError(err)) {

        throw err;
      }
      if (err.response?.status === notFound || err.response?.status === conflict) {
        // we return here because we do not want to reveal if the user has an email registered or not,
        //  to prevent customer data leakage
        return;
      }

      throw err;
    }
  }

  public async verifyPasscode({ email, passcode }: VerifyLoginPasscodeDto):
  Promise<LoginSuccessResponse | void> {
    const data: VerifyLoginPasscodeDto = { email, passcode };
    const result = await this.axios.put("login-passcode", data);
    return result.data;
  }

  public async useMagicLink(code: string): Promise<LoginSuccessResponse | MagicLinkInvalidResponse> {
    const data: UseMagicLinkRequest = { code };

    try {
      const response = await this.axios.post<LoginSuccessResponse>("magic-link", data);
      return response.data;
    } catch (err) {
      if (Axios.isAxiosError(err)) {
        const errorData = err.response?.data as MagicLinkInvalidResponse;
        return errorData;
      }
      throw err;
    }
  }

  public async getAccessTokenFromRefreshToken(
    refreshToken: string,
  ): Promise<ITokenSuccessResponse> {

    const data: TokenRequest = { grant_type: "refresh_token", refresh_token: refreshToken };
    const response = await this.axios.post<ITokenSuccessResponse>(
      "token",
      data,
    );

    return response.data;
  }

  public async createUser(
    userRequest: CreateUserRequest,
  ): Promise<CreateUserResponse> {
    const response = await this.axios.post<CreateUserResponse>(
      "create-user",
      userRequest,
    );

    return response.data;
  }

  public static decodePayload(jwt: AccessToken): JsonWebTokenPayload | undefined {
    try {
      return JWT.decode(jwt) as JsonWebTokenPayload;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Invalid JWT format", err);
      return undefined;
    }
  }

  public async getChatToken(accessToken: string, phoneNumber: string, name: string): Promise<string> {
    const response = await this.axios.get("chat-token", {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        phoneNumber,
        name,
      },
    });
    return response.data;
  }

}
